import type { HealthStatus } from "../appliance/health";
import type { FleetHealthEntry } from "../appliance/useFleetHealth";

/**
 * Pure model of the opsanio › Health wall: URL settings, ordering and the
 * change markers between two refreshes. Kept free of React and of runtime
 * imports so the root `npm test` harness can load it directly.
 *
 * Everything is derived from the current PRTG snapshot. "New" and "recovered"
 * compare the last two sweeps of this browser session only - nothing is stored.
 */

/** Production waves; the wall shows these unless `?waves=` says otherwise. */
export const WALL_DEFAULT_WAVES = ["main", "wave1", "wave2", "wave3"];
/** Rollout order (mirrors WAVE_ORDER in ../appliance/waves, kept import-free). */
const KNOWN_WAVES = ["dev", "qa", "main", "wave1", "wave2", "wave3", "debug"];

export const WALL_DEFAULT_INTERVAL = 60;
export const WALL_MIN_INTERVAL = 15;
export const WALL_MAX_INTERVAL = 3600;
/** Retry cadence while PRTG is unreachable for the whole fleet. */
export const WALL_RETRY_SECONDS = 15;
/** The appliance list itself changes rarely - reload it every 15 minutes. */
export const WALL_LIST_REFRESH_MS = 15 * 60 * 1000;

export interface WallSettings {
  /** Seconds between two health sweeps. */
  interval: number;
  /** Waves shown; null = every wave. */
  waves: string[] | null;
  /** Hide header + side nav (wall screen). */
  kiosk: boolean;
  /** Page zoom in kiosk mode, so OUI's desktop sizes read from a distance. */
  zoom: number;
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

/**
 * Read the wall settings from the query string, so a bookmarked wall URL
 * (e.g. `?kiosk=1&interval=30&waves=main,wave1&zoom=1.25`) keeps them.
 */
export const parseWallSettings = (
  params: URLSearchParams | string,
): WallSettings => {
  const p = typeof params === "string" ? new URLSearchParams(params) : params;

  const rawInterval = Number.parseInt(p.get("interval") ?? "", 10);
  const interval = Number.isFinite(rawInterval)
    ? clamp(rawInterval, WALL_MIN_INTERVAL, WALL_MAX_INTERVAL)
    : WALL_DEFAULT_INTERVAL;

  const rawWaves = (p.get("waves") ?? "").trim().toLowerCase();
  let waves: string[] | null = WALL_DEFAULT_WAVES;
  if (rawWaves === "all") {
    waves = null;
  } else if (rawWaves !== "") {
    const picked = KNOWN_WAVES.filter((w) =>
      rawWaves.split(",").map((x) => x.trim()).includes(w),
    );
    if (picked.length > 0) waves = picked;
  }

  const kiosk = ["1", "true", "yes"].includes(
    (p.get("kiosk") ?? "").toLowerCase(),
  );

  const rawZoom = Number.parseFloat(p.get("zoom") ?? "");
  const zoom = Number.isFinite(rawZoom) ? clamp(rawZoom, 0.5, 3) : 1;

  return { interval, waves, kiosk, zoom };
};

/** Higher = worse. Unmonitored ranks above healthy: it is not a green signal. */
export const severityRank = (status: HealthStatus): number =>
  ({ critical: 3, warning: 2, unknown: 1, healthy: 0 })[status];

const isIncident = (status: HealthStatus) =>
  status === "critical" || status === "warning";

const byName = (a: FleetHealthEntry, b: FleetHealthEntry) =>
  (a.appliance.displayName || a.appliance.id).localeCompare(
    b.appliance.displayName || b.appliance.id,
    "de",
  );

/** Critical first, then the lowest sensor score, then name. */
export const compareIncidents = (
  a: FleetHealthEntry,
  b: FleetHealthEntry,
): number =>
  severityRank(b.health.status) - severityRank(a.health.status) ||
  (a.health.score ?? 101) - (b.health.score ?? 101) ||
  byName(a, b);

/** Rollout position of a wave; unknown/empty waves sort last. */
const waveRank = (wave: string | null | undefined): number => {
  const i = KNOWN_WAVES.indexOf(wave ?? "");
  return i < 0 ? KNOWN_WAVES.length : i;
};

/** Wave in rollout order, then name. */
export const compareTiles = (
  a: FleetHealthEntry,
  b: FleetHealthEntry,
): number =>
  waveRank(a.appliance.wave) - waveRank(b.appliance.wave) || byName(a, b);

export type StatusMap = Record<string, HealthStatus>;

export const statusMap = (entries: FleetHealthEntry[]): StatusMap => {
  const out: StatusMap = {};
  for (const e of entries) out[e.appliance.id] = e.health.status;
  return out;
};

export interface WallChanges {
  /** Became critical/warning, or got worse, since the previous sweep. */
  newIds: string[];
  /** Was critical/warning at the previous sweep, healthy now. */
  recoveredIds: string[];
}

export const NO_CHANGES: WallChanges = { newIds: [], recoveredIds: [] };

/**
 * Compare two sweeps. The first sweep of a session has nothing to compare
 * against, so it marks nothing; an appliance that just appeared is not "new".
 */
export const diffStatuses = (
  previous: StatusMap | null,
  current: StatusMap,
): WallChanges => {
  if (!previous) return NO_CHANGES;
  const newIds: string[] = [];
  const recoveredIds: string[] = [];
  for (const [id, now] of Object.entries(current)) {
    const before = previous[id];
    if (before === undefined) continue;
    if (isIncident(now) && severityRank(now) > severityRank(before)) {
      newIds.push(id);
    } else if (isIncident(before) && now === "healthy") {
      recoveredIds.push(id);
    }
  }
  return { newIds, recoveredIds };
};

export interface WaveColumn {
  /** Wave name; "" for appliances without one. */
  wave: string;
  entries: FleetHealthEntry[];
}

/**
 * The fleet grid has one column per wave. Selected waves always get a column
 * (empty when all of its appliances are incidents) so the layout doesn't jump
 * between refreshes; with every wave selected, only the waves present show.
 * Columns follow the rollout order, tiles inside a column the name.
 */
export const fleetColumns = (
  tiles: FleetHealthEntry[],
  waves: string[] | null,
): WaveColumn[] => {
  const byWave = new Map<string, FleetHealthEntry[]>();
  for (const w of waves ?? []) byWave.set(w, []);
  for (const e of tiles) {
    const w = e.appliance.wave ?? "";
    if (!byWave.has(w)) byWave.set(w, []);
    byWave.get(w)!.push(e);
  }
  return Array.from(byWave.entries())
    .sort(([a], [b]) => waveRank(a) - waveRank(b))
    .map(([wave, entries]) => ({ wave, entries: [...entries].sort(byName) }));
};

export type TileNote = "prtgFailed" | "noName" | "noSensors" | null;

export interface WallModel {
  incidents: FleetHealthEntry[];
  tiles: FleetHealthEntry[];
  counts: Record<HealthStatus, number>;
  total: number;
}

export const buildWallModel = (entries: FleetHealthEntry[]): WallModel => {
  const counts: Record<HealthStatus, number> = {
    critical: 0,
    warning: 0,
    healthy: 0,
    unknown: 0,
  };
  const incidents: FleetHealthEntry[] = [];
  const tiles: FleetHealthEntry[] = [];
  for (const e of entries) {
    counts[e.health.status] += 1;
    (isIncident(e.health.status) ? incidents : tiles).push(e);
  }
  incidents.sort(compareIncidents);
  tiles.sort(compareTiles);
  return { incidents, tiles, counts, total: entries.length };
};

/** Why an unmonitored tile has no data. */
export const tileNote = (e: FleetHealthEntry): TileNote => {
  if (e.health.status !== "unknown") return null;
  if (e.prtgFailed) return "prtgFailed";
  if (!e.appliance.common?.metadata?.name?.trim()) return "noName";
  return "noSensors";
};

/**
 * How many sensor rows an incident card can show in a list box of `height` px.
 * All of them when they fit; otherwise as many as fit above the "+ N more"
 * line, but always at least one.
 */
export const alertsThatFit = (
  height: number,
  total: number,
  rowHeight: number,
  moreLineHeight: number,
): number => {
  if (total * rowHeight <= height) return total;
  return Math.max(1, Math.floor((height - moreLineHeight) / rowHeight));
};

/**
 * PRTG's `lastcheck` is a formatted string (date, time and a relative hint).
 * The wall only needs the time of day; returns null when there is none.
 */
export const checkTime = (lastCheck: string | null | undefined): string | null => {
  const m = /(\d{1,2}):(\d{2})/.exec(lastCheck ?? "");
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : null;
};
